import React, { useState } from "react";
import {
    HiArrowRight,
    HiArrowLeft,
    HiInformationCircle,
} from "react-icons/hi2";
import {
    HiAcademicCap,
    HiShoppingBag,
    HiHome,
    HiBriefcase,
    HiWrench,
    HiTruck,
    HiMusicalNote,
    HiBuildingLibrary,
    HiHeart,
    HiCube,
    HiSquare3Stack3D,
} from "react-icons/hi2";
import { FaParking } from "react-icons/fa";
import Button from "../../common/Button/Button";
import "./Step2.scss";

const Step2 = ({ onNext, onBack, initialData }) => {
    const [selectedTypes, setSelectedTypes] = useState(
        initialData?.buildingTypes || []
    );
    const [othersText, setOthersText] = useState(initialData?.othersText || "");
    const [error, setError] = useState("");
    const [activeTooltip, setActiveTooltip] = useState(null);

    React.useEffect(() => {
        const handleClickOutside = (event) => {
            if (activeTooltip !== null) {
                const isInfoButton = event.target.closest(
                    ".type-card__info-btn"
                );
                if (!isInfoButton) {
                    setActiveTooltip(null);
                }
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [activeTooltip]);

    const buildingTypes = [
        {
            id: "institutional",
            name: "Institucional",
            icon: HiAcademicCap,
            color: "#3b82f6",
            info: "Selecciona este tipo si tu proyecto es una universidad, escuela, edificio gubernamental, ayuntamiento, banco o cualquier institución educativa o administrativa.",
        },
        {
            id: "retail",
            name: "Comercial",
            icon: HiShoppingBag,
            color: "#41d31dff",
            info: "Ideal para centros comerciales, galerías, restaurantes, tiendas, locales comerciales y cualquier espacio destinado a la venta al público.",
        },
        {
            id: "residential",
            name: "Residencial",
            icon: HiHome,
            color: "#e42d88ff",
            info: "Para condominios, casas, apartamentos, complejos residenciales, hoteles, hostales y cualquier edificación destinada a vivienda u hospedaje.",
        },
        {
            id: "office",
            name: "Oficinas",
            icon: HiBriefcase,
            color: "#19c2e0ff",
            info: "Espacios de oficinas tradicionales, coworking, edificios corporativos, espacios de trabajo colaborativo y cualquier ambiente laboral.",
        },
        {
            id: "manufacturing",
            name: "Industrial",
            icon: HiWrench,
            color: "#f8a311ff",
            info: "Plantas de tratamiento, fábricas, instalaciones de producción, plantas industriales y cualquier edificación destinada a manufactura o procesos industriales.",
        },
        {
            id: "parking",
            name: "Estacionamiento",
            icon: FaParking,
            color: "#6366f1",
            info: "Estacionamientos al aire libre, garajes cubiertos, estructuras de parqueo de múltiples niveles y áreas de aparcamiento.",
        },
        {
            id: "transportation",
            name: "Transporte",
            icon: HiTruck,
            color: "#d6c808ff",
            info: "Estaciones de autobuses, terminales de tren, estaciones de metro, aeropuertos, torres de control y cualquier infraestructura de transporte.",
        },
        {
            id: "entertainment",
            name: "Entretenimiento",
            icon: HiMusicalNote,
            color: "#f43f5e",
            info: "Teatros, cines, museos, galerías de arte, estadios deportivos, arenas y cualquier espacio destinado al entretenimiento y cultura.",
        },
        {
            id: "patrimonial",
            name: "Patrimonial",
            icon: HiBuildingLibrary,
            color: "#65a506ff",
            info: "Iglesias, catedrales, edificios históricos, monumentos, construcciones con valor patrimonial y cualquier estructura de importancia cultural.",
        },
        {
            id: "healthcare",
            name: "Salud",
            icon: HiHeart,
            color: "#ef4444",
            info: "Hospitales, clínicas, centros médicos, residencias de ancianos, centros de rehabilitación y cualquier instalación de salud.",
        },
        {
            id: "warehouse",
            name: "Almacén",
            icon: HiSquare3Stack3D,
            color: "#29d8c4ff",
            info: "Centros de distribución, almacenes, bodegas, plantas de logística y cualquier espacio destinado al almacenamiento y distribución.",
        },
        {
            id: "others",
            name: "Otros",
            icon: HiCube,
            color: "#64748b",
            info: "Si tu proyecto no encaja en ninguna de las categorías anteriores, selecciona esta opción y especifica el tipo de edificio.",
        },
    ];

    const handleTypeToggle = (typeId) => {
        setSelectedTypes((prev) => {
            if (prev.includes(typeId)) {
                return prev.filter((id) => id !== typeId);
            } else {
                return [...prev, typeId];
            }
        });

        if (error) setError("");
    };

    const handleInfoClick = (e, typeId) => {
        e.stopPropagation();
        setActiveTooltip(activeTooltip === typeId ? null : typeId);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (selectedTypes.length === 0) {
            setError("Selecciona al menos un tipo de edificio");
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        if (selectedTypes.includes("others") && !othersText.trim()) {
            setError('Por favor especifica el tipo en "Otros"');
            window.scrollTo({ top: 0, behavior: "smooth" });
            return;
        }

        onNext({ buildingTypes: selectedTypes, othersText });
    };

    return (
        <div className="step2">
            <div className="step2__container">
                <form className="step2__form" onSubmit={handleSubmit}>
                    <div className="form-section">
                        <div className="section-header">
                            <h3 className="section-title">
                                ¿Qué tipo de edificio o espacio es?
                            </h3>
                            <p className="section-subtitle">
                                Selecciona uno o varios tipos que describan tu
                                proyecto
                            </p>
                        </div>

                        {error && <p className="error-message">{error}</p>}

                        <div className="types-grid">
                            {buildingTypes.map((type) => {
                                const Icon = type.icon;
                                const isSelected = selectedTypes.includes(
                                    type.id
                                );
                                const showTooltip = activeTooltip === type.id;

                                return (
                                    <div
                                        key={type.id}
                                        className="type-card-wrapper"
                                    >
                                        <button
                                            type="button"
                                            className={`type-card ${
                                                isSelected
                                                    ? "type-card--selected"
                                                    : ""
                                            }`}
                                            onClick={() =>
                                                handleTypeToggle(type.id)
                                            }
                                            style={{
                                                "--type-color": type.color,
                                            }}
                                        >
                                            <button
                                                type="button"
                                                className="type-card__info-btn"
                                                onClick={(e) =>
                                                    handleInfoClick(e, type.id)
                                                }
                                                aria-label="Más información"
                                            >
                                                <HiInformationCircle />
                                            </button>

                                            <div className="type-card__icon">
                                                <Icon />
                                            </div>
                                            <span className="type-card__name">
                                                {type.name}
                                            </span>

                                            {isSelected && (
                                                <div className="type-card__checkmark">
                                                    ✓
                                                </div>
                                            )}
                                        </button>

                                        {showTooltip && (
                                            <div className="tooltip">
                                                <div className="tooltip__arrow"></div>
                                                <p className="tooltip__text">
                                                    {type.info}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {selectedTypes.includes("others") && (
                            <div className="others-input">
                                <label htmlFor="othersText">
                                    Especifica el tipo de edificio *
                                </label>
                                <input
                                    type="text"
                                    id="othersText"
                                    value={othersText}
                                    onChange={(e) =>
                                        setOthersText(e.target.value)
                                    }
                                    placeholder="Ej: Centro deportivo, aeropuerto, estadio..."
                                    maxLength={100}
                                />
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <Button
                            variant="outline"
                            icon={<HiArrowLeft />}
                            animationType="pop"
                            onClick={(e) => {
                                e.preventDefault();
                                onBack();
                            }}
                        >
                            Volver
                        </Button>

                        <Button
                            variant="filled"
                            icon={<HiArrowRight />}
                            animationType="pop"
                            disabled={selectedTypes.length === 0}
                        >
                            Continuar
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default Step2;
